import CondorSpinner from '../CondorSpinner';
import { SectionTitleText } from '../../StyledComponents/Typography';
import cutecondor from '../../assets/cute-condor.png';
import React, { useCallback, useMemo } from 'react';
import { StyledDataGrid } from '../../StyledComponents/Table';
import { ButtonRules, StyledButton } from '@canopysecurity/component-library';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useRoleAuthorization } from '../../hooks/RoleAuthorization';
import { GridValidRowModel } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { modalStateHook } from '../ModalStateHook';
import { PromoteModal } from './PromoteModal';
import { SetStableModal } from './SetStableModal';
import { useQuery } from '@tanstack/react-query';
import { getReleases, Release } from '../../services/ReleaseService';
import { Artifact } from '../../services/ArtifactService';

const TableContainer = styled.div`
  margin-top: 3%;
  padding: 0 5% 0 5%;
  height: 40vh;
  margin-bottom: 4em;

  .header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
  }

  .button-container {
    display: flex;
    justify-content: flex-end;
    column-gap: 8px;
  }
`;

const { useModalState } = modalStateHook();

export interface ReleaseTableRow {
  id: string;
  semver: string;
  isPromoted: boolean;
  isStable: boolean;
  isDeployed: boolean;
  artifactIds: string[];
  artifacts: Artifact[];
  deviceType: string;
}

const DeployButton = React.memo<{
  release: ReleaseTableRow;
  onDeploy: (release: ReleaseTableRow) => void;
}>(({ release, onDeploy }) => {
  const handleClick = useCallback(() => {
    onDeploy(release);
  }, [onDeploy, release]);

  return (
    <StyledButton
      onPress={handleClick}
      buttonRules={ButtonRules.primaryA}
      testID={`deploy-release-button-${release.id}`}
    >
      Deploy
    </StyledButton>
  );
});
DeployButton.displayName = 'DeployButton';

const PromoteButton = React.memo<{
  release: ReleaseTableRow;
  onPromote: (release: ReleaseTableRow) => void;
}>(({ release, onPromote }) => {
  const { canPromotePackages } = useRoleAuthorization();
  const handleClick = useCallback(() => {
    onPromote(release);
  }, [onPromote, release]);
  return (
    <>
      {canPromotePackages() ? (
        <StyledButton
          onPress={handleClick}
          disabled={release.isPromoted}
          buttonRules={ButtonRules.primaryB}
          testID={`promote-button-${release.id}`}
        >
          Promote
        </StyledButton>
      ) : (
        <></>
      )}
    </>
  );
});
PromoteButton.displayName = 'PromoteButton';
const SetStableButton = React.memo<{
  release: ReleaseTableRow;
  onSetStable: (release: ReleaseTableRow) => void;
}>(({ release, onSetStable }) => {
  const { canSetStable } = useRoleAuthorization();

  const handleClick = useCallback(() => {
    onSetStable(release);
  }, [onSetStable, release]);
  return (
    <>
      {canSetStable() && release.isDeployed ? (
        <StyledButton
          onPress={handleClick}
          disabled={release.isStable}
          buttonRules={ButtonRules.primaryA}
          testID={`set-stable-button-${release.id}`}
        >
          Set Stable
        </StyledButton>
      ) : (
        <></>
      )}
    </>
  );
});
SetStableButton.displayName = 'SetStableButton';

export default function ReleasePackages({
  triggerRefresh,
}: {
  triggerRefresh: () => void;
}) {
  const navigate = useNavigate();
  const [selectedRelease, setSelectedRelease] = React.useState<Release | null>(
    null,
  );
  const promotionModal = useModalState();
  const stableModal = useModalState();

  const {
    data,
    isPending: loading,
    refetch,
  } = useQuery({
    queryKey: ['releases'],
    queryFn: getReleases,
  });

  const handleRefresh = async () => {
    await refetch();
    triggerRefresh();
  };
  const handleSetStable = (release: ReleaseTableRow) => {
    setSelectedRelease(release as Release);
    stableModal.open();
  };
  const handleSetPromoted = (release: ReleaseTableRow) => {
    setSelectedRelease(release as Release);
    promotionModal.open();
  };
  const handleDeployRelease = (release: ReleaseTableRow) => {
    navigate('/deploy-release-package', {
      state: release,
    });
  };

  const releases = useMemo(() => {
    return data
      ?.filter((release) => release !== undefined)
      .map((release) => {
        return {
          id: release?.id,
          semver: release?.semver,
          isPromoted: release?.isPromoted,
          isStable: release?.isStable ?? false,
          isDeployed: release?.isDeployed,
          artifacts: release?.artifacts,
          artifactIds: release?.artifactIds,
          deviceType: release?.deviceType,
        };
      });
  }, [data]);
  const createColumns = (): GridColDef<ReleaseTableRow>[] => [
    {
      field: 'setStable',
      headerName: '',
      width: 300,
      renderCell: (params) => (
        <SetStableButton release={params.row} onSetStable={handleSetStable} />
      ),
    },
    {
      field: 'semver',
      flex: 1,
      renderHeader: () => <strong>Release Version</strong>,
    },
    {
      field: 'setPromoted',
      headerName: '',
      width: 150,
      renderCell: (params) => (
        <PromoteButton release={params.row} onPromote={handleSetPromoted} />
      ),
    },
    {
      field: 'deploy',
      headerName: '',
      width: 150,
      renderCell: (params) => (
        <DeployButton release={params.row} onDeploy={handleDeployRelease} />
      ),
    },
  ];

  const columns = useMemo(() => createColumns(), [data]);

  return (
    <>
      {loading ? (
        <CondorSpinner />
      ) : (
        <>
          <TableContainer>
            <div className="release-header-container">
              <SectionTitleText>OTA Packages</SectionTitleText>
            </div>

            <StyledDataGrid
              rows={releases}
              columns={columns as GridColDef<GridValidRowModel>[]}
              loading={loading}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
              }
              slots={{
                noRowsOverlay: () => (
                  <div>
                    <img
                      src={cutecondor}
                      alt="No releases to display"
                      style={{ maxHeight: '70%' }}
                    />
                  </div>
                ),
              }}
              pageSizeOptions={[5, 10, 50, 100]}
            />
          </TableContainer>
          {selectedRelease && (
            <>
              <PromoteModal
                isVisible={promotionModal.isVisible}
                release={selectedRelease as Release}
                onClose={promotionModal.close}
                refetch={refetch}
              />
              <SetStableModal
                isVisible={stableModal.isVisible}
                release={selectedRelease as Release}
                onClose={stableModal.close}
                refetch={handleRefresh}
              />
            </>
          )}
        </>
      )}
    </>
  );
}
